"use client";

import React, { createContext, useContext } from "react";
import { useUser, useAuth, useOrganizationList } from "@clerk/nextjs";
import type { UserResource } from "@clerk/nextjs/types";
import { hasFreePro } from "@/lib/client/user";

type AuthContextType = {
  isProUser: boolean;
  // The raw Clerk plan check, before OR-ing in admin/free-pro grants —
  // isProUser alone can't tell a real paid conversion apart from an admin
  // account or a comped free-pro grant, which matters for anything that
  // needs to fire only on an actual purchase (see TrackProSubscription).
  isPaidProUser: boolean;
  canToggleVisibility: boolean;
  user: UserResource | null;
  userId: string | null;
  isAdmin: boolean;
  isTester: boolean;
  isSignedIn: boolean;
  isLoaded: boolean;
};

const AuthContext = createContext<AuthContextType>({
  isProUser: false,
  isPaidProUser: false,
  canToggleVisibility: false,
  user: null,
  userId: null,
  isAdmin: false,
  isTester: false,
  isSignedIn: false,
  isLoaded: false,
});

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isLoaded } = useUser();
  const { has } = useAuth();
  const { userMemberships } = useOrganizationList({ userMemberships: { infinite: false } });

  const resolvedUser = user ?? null;
  const isProUser = isLoaded ? (has?.({ plan: "pro" }) ?? false) : false;
  const isAdmin =
    isLoaded && userMemberships.data != null
      ? userMemberships.data.some((m) => m.organization.id === process.env.NEXT_PUBLIC_ADMIN_ORG_ID)
      : false;
  const isTester = resolvedUser?.publicMetadata?.tester === true;
  const isFreePro = hasFreePro(resolvedUser);
  const effectiveIsProUser = isProUser || isFreePro || isAdmin;

  return (
    <AuthContext.Provider
      value={{
        isProUser: effectiveIsProUser,
        isPaidProUser: isProUser,
        user: resolvedUser,
        userId: resolvedUser?.id ?? null,
        canToggleVisibility: effectiveIsProUser,
        isAdmin,
        isTester,
        isSignedIn: !!resolvedUser,
        isLoaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
